import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import {
    ArrowRight,
    CloseCircle,
    DocumentText,
    Eye,
    LampOn,
    Messages,
    Profile2User,
    Share,
    TaskSquare,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import { get } from "../../../../controlers/global/ConfigRequest";
import { COMPANY_CLUB_ACTIVITY } from "../../../../helperData/ApiAddress";
import {
    formatCount,
    formatPhone,
    memberDisplayName,
    memberInitials,
} from "./helpers";

const KIND_ICONS = {
    message: Messages,
    idea: LampOn,
    referral: Share,
    content_share: DocumentText,
    content_view: Eye,
    survey: TaskSquare,
};

const KIND_LABELS = {
    message: "Organizational.CustomerClub.Members.Kind.Message",
    idea: "Organizational.CustomerClub.Members.Kind.Idea",
    referral: "Organizational.CustomerClub.Members.Kind.Referral",
    content_share: "Organizational.CustomerClub.Members.Kind.ContentShare",
    content_view: "Organizational.CustomerClub.Members.Kind.ContentView",
    survey: "Organizational.CustomerClub.Members.Kind.Survey",
};

const STATS = [
    ["messages", "Organizational.CustomerClub.Members.Metric.Messages"],
    ["ideas", "Organizational.CustomerClub.Members.Metric.Ideas"],
    ["referral_opens", "Organizational.CustomerClub.Members.Metric.Referrals"],
    ["content_views", "Organizational.CustomerClub.Members.Metric.Views"],
    ["coins_earned", "Organizational.CustomerClub.Members.Metric.Coins"],
    ["surveys", "Organizational.CustomerClub.Members.Metric.Surveys"],
];

function activityKey(activity) {
    return `${activity.kind}-${activity.id}`;
}

function opensMessage(activity) {
    return Boolean(
        activity?.opens_message ||
            activity?.kind === "message" ||
            activity?.kind === "idea"
    );
}

function PeopleList({ people, emptyLabel }) {
    if (!people?.length) {
        return <p className="member-dossier-empty-note">{emptyLabel}</p>;
    }
    return (
        <ul className="member-dossier-people">
            {people.map((person) => (
                <li key={person.id || person.phone}>
                    <span>{person.name || "—"}</span>
                    <span dir="ltr">{formatPhone(person.phone)}</span>
                </li>
            ))}
        </ul>
    );
}

function ActivityDetail({ activity, t }) {
    const detail = activity.detail || {};
    if (activity.kind === "referral") {
        return (
            <>
                <p className="member-dossier-detail-copy">
                    {detail.description ||
                        t("Organizational.CustomerClub.Members.Dossier.NoText")}
                </p>
                <p className="member-dossier-section">
                    {t("Organizational.CustomerClub.Members.Dossier.Openers")}
                </p>
                <PeopleList
                    people={detail.openers}
                    emptyLabel={t(
                        "Organizational.CustomerClub.Members.Dossier.NoOpeners"
                    )}
                />
            </>
        );
    }
    if (activity.kind === "content_share" || activity.kind === "content_view") {
        return (
            <>
                <p className="member-dossier-detail-copy">
                    {detail.description ||
                        t("Organizational.CustomerClub.Members.Dossier.NoText")}
                </p>
                {detail.url ? (
                    <a
                        className="member-dossier-link"
                        href={detail.url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {detail.url}
                    </a>
                ) : null}
                {activity.kind === "content_share" ? (
                    <>
                        <p className="member-dossier-section">
                            {t(
                                "Organizational.CustomerClub.Members.Dossier.Viewers"
                            )}
                        </p>
                        <PeopleList
                            people={detail.viewers}
                            emptyLabel={t(
                                "Organizational.CustomerClub.Members.Dossier.NoViewers"
                            )}
                        />
                    </>
                ) : null}
            </>
        );
    }
    if (activity.kind === "survey") {
        const replies = detail.replies || [];
        const features = detail.features || [];
        return (
            <>
                <p className="member-dossier-status">
                    {detail.completed
                        ? t(
                              "Organizational.CustomerClub.Members.Dossier.SurveyDone"
                          )
                        : t(
                              "Organizational.CustomerClub.Members.Dossier.SurveyOpen"
                          )}
                </p>
                {replies.length ? (
                    <ul className="member-dossier-replies">
                        {replies.map((reply, index) => (
                            <li key={`${reply.question}-${index}`}>
                                <span>{reply.question || "—"}</span>
                                <strong>
                                    {reply.answer || reply.description || "—"}
                                </strong>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="member-dossier-empty-note">
                        {t(
                            "Organizational.CustomerClub.Members.Dossier.NoReplies"
                        )}
                    </p>
                )}
                {features.length ? (
                    <ul className="member-dossier-replies">
                        {features.map((feature, index) => (
                            <li key={`${feature.name}-${index}`}>
                                <span>{feature.name || "—"}</span>
                                <strong>{feature.value || "—"}</strong>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </>
        );
    }
    return (
        <p className="member-dossier-detail-copy">
            {detail.description ||
                activity.summary ||
                t("Organizational.CustomerClub.Members.Dossier.NoText")}
        </p>
    );
}

export default function MemberDetailModal({
    member,
    show,
    onHide,
    onOpenMessage,
    locale = "fa-IR",
}) {
    const { t } = useTranslation();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const fallbackName = memberDisplayName(member || {});
    const profile = payload?.profile;
    const name = profile?.name || fallbackName;

    useEffect(() => {
        if (!show || !member?.id) {
            setPayload(null);
            setSelected(null);
            setError("");
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError("");
        setSelected(null);
        get(COMPANY_CLUB_ACTIVITY.replace("{id}", member.id))
            .then((response) => {
                if (!cancelled) setPayload(response);
            })
            .catch(() => {
                if (!cancelled) {
                    setPayload(null);
                    setError(
                        t("Organizational.CustomerClub.Members.Dossier.LoadError")
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [show, member?.id, t]);

    const handleActivity = (activity) => {
        if (opensMessage(activity)) {
            onOpenMessage(activity.source_id || activity.id);
            return;
        }
        setSelected(activity);
    };

    const activities = payload?.activities || [];
    const stats = payload?.stats || {};

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className="member-dossier-modal"
            aria-labelledby="member-dossier-title"
        >
            <Modal.Body>
                <article className="member-dossier">
                    <header className="member-dossier-card">
                        <span className="member-avatar" aria-hidden="true">
                            {memberInitials(name)}
                        </span>
                        <div className="member-dossier-card-copy">
                            <p className="member-dossier-kicker">
                                {t(
                                    "Organizational.CustomerClub.Members.Dossier.Kicker"
                                )}
                            </p>
                            <h2 id="member-dossier-title">{name}</h2>
                            <p>
                                <span dir="ltr">
                                    {formatPhone(
                                        profile?.phone ||
                                            member?.user?.phone_number
                                    )}
                                </span>
                                {profile?.joined_at_jalali ? (
                                    <>
                                        <span aria-hidden="true"> · </span>
                                        <span>
                                            {t(
                                                "Organizational.CustomerClub.Members.Dossier.Joined",
                                                {
                                                    date: profile.joined_at_jalali,
                                                }
                                            )}
                                        </span>
                                    </>
                                ) : null}
                            </p>
                        </div>
                        <div className="member-dossier-card-meta">
                            {profile?.group_name ? (
                                <span className="member-dossier-chip">
                                    {profile.group_name}
                                </span>
                            ) : null}
                            <button
                                type="button"
                                className="member-icon-btn"
                                onClick={onHide}
                                aria-label={t(
                                    "Organizational.CustomerClub.Members.Dossier.Close"
                                )}
                            >
                                <CloseCircle size={18} aria-hidden="true" />
                            </button>
                        </div>
                    </header>

                    <ul className="member-dossier-tally" aria-label={t("Organizational.CustomerClub.Members.Dossier.Tally")}>
                        {STATS.map(([key, label]) => (
                            <li key={key}>
                                <span>{t(label)}</span>
                                <strong className="tabular-nums">
                                    {loading
                                        ? "—"
                                        : formatCount(stats[key], locale)}
                                </strong>
                            </li>
                        ))}
                    </ul>

                    {selected ? (
                        <section
                            className="member-dossier-pane"
                            aria-labelledby="member-dossier-pane-title"
                        >
                            <button
                                type="button"
                                className="member-dossier-back"
                                onClick={() => setSelected(null)}
                            >
                                <ArrowRight size={16} aria-hidden="true" />
                                {t(
                                    "Organizational.CustomerClub.Members.Dossier.Back"
                                )}
                            </button>
                            <p className="member-dossier-kind">
                                {t(
                                    KIND_LABELS[selected.kind] ||
                                        KIND_LABELS.message
                                )}
                            </p>
                            <h3 id="member-dossier-pane-title">
                                {selected.title ||
                                    t(
                                        KIND_LABELS[selected.kind] ||
                                            KIND_LABELS.message
                                    )}
                            </h3>
                            {selected.occurred_at_jalali ? (
                                <p className="member-dossier-when tabular-nums">
                                    {selected.occurred_at_jalali}
                                </p>
                            ) : null}
                            <ActivityDetail activity={selected} t={t} />
                        </section>
                    ) : (
                        <section
                            className="member-dossier-ledger"
                            aria-label={t(
                                "Organizational.CustomerClub.Members.Dossier.Ledger"
                            )}
                        >
                            {loading ? (
                                <div className="member-loading" role="status">
                                    <Spinner animation="border" size="sm" />
                                    <span>
                                        {t(
                                            "Organizational.CustomerClub.Members.Dossier.Loading"
                                        )}
                                    </span>
                                </div>
                            ) : error ? (
                                <div className="member-empty" role="alert">
                                    <span>{error}</span>
                                </div>
                            ) : !activities.length ? (
                                <div className="member-empty">
                                    <Profile2User size={28} aria-hidden="true" />
                                    <strong>
                                        {t(
                                            "Organizational.CustomerClub.Members.Dossier.Empty"
                                        )}
                                    </strong>
                                    <span>
                                        {t(
                                            "Organizational.CustomerClub.Members.Dossier.Empty.Hint"
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <ol className="member-ledger">
                                    {activities.map((activity) => {
                                        const Icon =
                                            KIND_ICONS[activity.kind] ||
                                            DocumentText;
                                        return (
                                            <li key={activityKey(activity)}>
                                                <button
                                                    type="button"
                                                    className="member-ledger-row"
                                                    onClick={() =>
                                                        handleActivity(activity)
                                                    }
                                                >
                                                    <span className="member-ledger-when tabular-nums">
                                                        {activity.occurred_at_jalali ||
                                                            "—"}
                                                    </span>
                                                    <span
                                                        className={`member-ledger-stamp is-${activity.kind}`}
                                                    >
                                                        <Icon
                                                            size={14}
                                                            aria-hidden="true"
                                                        />
                                                        {t(
                                                            KIND_LABELS[
                                                                activity.kind
                                                            ] ||
                                                                KIND_LABELS.message
                                                        )}
                                                    </span>
                                                    <span className="member-ledger-copy">
                                                        <strong>
                                                            {activity.title ||
                                                                t(
                                                                    KIND_LABELS[
                                                                        activity
                                                                            .kind
                                                                    ] ||
                                                                        KIND_LABELS.message
                                                                )}
                                                        </strong>
                                                        <span>
                                                            {activity.summary ||
                                                                t(
                                                                    "Organizational.CustomerClub.Members.Dossier.OpenItem"
                                                                )}
                                                        </span>
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ol>
                            )}
                        </section>
                    )}
                </article>
            </Modal.Body>
        </Modal>
    );
}
